// Preview ao vivo da identidade.
//
// A resolução acontece no servidor (POST /identity/preview) e não aqui: a
// matemática de contraste vive num lugar só, e o preview mostra exatamente o
// que será salvo — inclusive quando o validador recusa a combinação.
const DEBOUNCE_MS = 250

function initIdentityPreview() {
    const form = document.getElementById("identity-form")
    const target = document.getElementById("identity-preview")
    if (!form || !target) return

    const status = form.querySelector("[data-preview-status]")
    const problems = form.querySelector("[data-preview-problems]")
    let style = document.getElementById("identity-preview-style")
    let timer = null
    let inFlight = null

    if (!style) {
        style = document.createElement("style")
        style.id = "identity-preview-style"
        document.head.appendChild(style)
    }

    // Picker e campo de texto editam o mesmo hex; um espelha o outro.
    //
    // Os dois eventos, sempre. `input` sozinho não basta: o seletor de cor
    // nativo nem sempre o emite — dependendo do navegador e do sistema, escolher
    // no painel e fechá-lo produz só `change`. Sem escutar os dois, a cor só
    // aparecia quando o campo se resolvia depois, e o ato de escolher não
    // mudava nada. O debounce absorve o caso em que os dois disparam.
    const SYNCED = ["input", "change"]

    form.querySelectorAll("[data-color-sync]").forEach((picker) => {
        const key = picker.dataset.colorSync
        const text = form.querySelector(`[data-color-sync-text="${key}"]`)
        if (!text) return

        SYNCED.forEach((event) => {
            picker.addEventListener(event, () => {
                text.value = picker.value
                schedule()
            })
            text.addEventListener(event, () => {
                if (/^#[0-9a-fA-F]{6}$/.test(text.value)) picker.value = text.value
                schedule()
            })
        })
    })

    connectPreviewToFields()

    // Liga os dois sentidos entre prévia e formulário.
    //
    // O formulário sozinho não diz o que cada campo controla: descobrir que
    // "Tinta e blocos escuros" muda o título da página era tentativa e erro.
    // Agora clicar na prévia leva ao campo, e focar o campo destaca o que ele
    // controla na prévia.
    function connectPreviewToFields() {
        // Prévia -> campo
        target.addEventListener("click", (event) => {
            const region = event.target.closest("[data-edits]")
            if (!region) return

            event.preventDefault()
            const field = fieldFor(region.dataset.edits)
            if (!field) return

            field.scrollIntoView({ block: "center", behavior: "smooth" })
            field.focus({ preventScroll: true })
            flash(field)

            // Abre o seletor nativo quando existe um: é o passo que o usuário
            // ia dar em seguida de qualquer forma.
            const picker = form.querySelector(`[data-color-sync="${region.dataset.edits}"]`)
            picker?.showPicker?.()
        })

        // Campo -> prévia
        //
        // Todo controle da paleta é hoje um par picker + texto; o `select` que
        // existia aqui saiu junto com o lookup de destaque da navegação.
        form.querySelectorAll("[data-color-sync-text]").forEach((field) => {
            const key = field.dataset.colorSyncText
            const regions = target.querySelectorAll(`[data-edits="${key}"]`)
            if (!regions.length) return

            const on = () => regions.forEach((r) => r.classList.add("is-target"))
            const off = () => regions.forEach((r) => r.classList.remove("is-target"))

            field.addEventListener("focus", on)
            field.addEventListener("blur", off)
            field.addEventListener("mouseenter", on)
            field.addEventListener("mouseleave", off)
        })
    }

    function fieldFor(key) {
        return (
            form.querySelector(`[data-color-sync-text="${key}"]`) ??
            form.querySelector(`[name="company_theme[${key}]"]`)
        )
    }

    function flash(field) {
        field.classList.remove("identity-field-flash")
        void field.offsetWidth // reinicia a animação quando é o mesmo campo
        field.classList.add("identity-field-flash")
    }

    // Só os campos da paleta.
    //
    // Mandar o FormData inteiro não funciona: o formulário é `method: :patch`,
    // que o Rails implementa com um campo oculto `_method`. Indo junto no
    // corpo, o Rails reinterpretava este POST como PATCH e não achava rota —
    // o preview respondia 404 e a prévia só mudava ao salvar.
    //
    // Filtrar também evita mandar os arquivos de logo (que viram
    // "[object File]" ao serem serializados) num endpoint que nem os lê.
    function previewParams() {
        const params = new URLSearchParams()

        for (const [name, value] of new FormData(form)) {
            if (name.startsWith("company_theme")) params.append(name, value)
        }

        return params
    }

    function schedule() {
        clearTimeout(timer)
        timer = setTimeout(refresh, DEBOUNCE_MS)
    }

    async function refresh() {
        inFlight?.abort()
        inFlight = new AbortController()

        try {
            const response = await fetch(form.dataset.previewUrl, {
                method: "POST",
                signal: inFlight.signal,
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content ?? "",
                    Accept: "application/json",
                },
                body: previewParams(),
            })
            if (!response.ok) return

            const data = await response.json()
            apply(data)
        } catch (err) {
            if (err.name !== "AbortError") console.error(err)
        }
    }

    function apply(data) {
        // Escopa os tokens ao bloco de preview: a página em volta continua na
        // paleta salva, então dá para comparar antes e depois lado a lado.
        style.textContent = data.css.replaceAll(":root", "#identity-preview")

        target.classList.toggle("opacity-40", !data.valid)

        if (status) {
            // Mantém a dica do clique quando está tudo certo: é a única pista
            // de que a prévia é interativa.
            status.textContent = data.valid
                ? "Clique em qualquer parte para editar a cor dela."
                : "Esta combinação será recusada:"
        }

        if (problems) {
            problems.innerHTML = ""
            problems.classList.toggle("hidden", data.valid)
            problems.classList.toggle("text-error", !data.valid)
            data.problems.slice(0, 6).forEach((message) => {
                const li = document.createElement("li")
                li.textContent = message
                problems.appendChild(li)
            })
        }
    }
}

// Turbo Drive está desligado neste projeto (ver js/turbo.js), então a carga
// normal da página é o único momento de inicializar.
document.addEventListener("DOMContentLoaded", initIdentityPreview)
